import { memo, useCallback } from 'react';
import {
  cartContainerInteractiveClass,
  cartContainerRootClass,
  cartContainerSizeClass,
  normalizeCartContainerSize,
  normalizeCartContainerState,
  normalizeCartContainerVariant,
  resolveCartContainerSurface,
} from './cartContainerClasses.js';

/**
 * Card Component — Figma DS AIMS OS node 5388:23471.
 * Bordered surface that wraps {@link EntityList} rows (and other slot content).
 *
 * @example
 * <CartContainer variant="primary" selected>
 *   <EntityList title="Follow-up call" description="..." />
 * </CartContainer>
 */
const CartContainer = memo(({
  size = 'L',
  variant = 'neutral',
  state = 'default',
  selected = false,
  disabled = false,
  children,
  className = '',
  onClick,
  ...props
}) => {
  const normalizedSize = normalizeCartContainerSize(size);
  const normalizedVariant = normalizeCartContainerVariant(variant);
  const normalizedState = normalizeCartContainerState(state, { selected, disabled });
  const isInteractive = typeof onClick === 'function' && normalizedState !== 'disabled';
  const sizeStyles = cartContainerSizeClass[normalizedSize];
  const surfaceClass = resolveCartContainerSurface(normalizedVariant, normalizedState, {
    interactive: normalizedState === 'default',
  });

  const handleKeyDown = useCallback(
    (event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onClick(event);
      }
    },
    [onClick],
  );

  return (
    <div
      role={isInteractive ? 'button' : undefined}
      tabIndex={isInteractive ? 0 : undefined}
      onClick={isInteractive ? onClick : undefined}
      onKeyDown={isInteractive ? handleKeyDown : undefined}
      className={[
        cartContainerRootClass,
        sizeStyles.padding,
        sizeStyles.radius,
        surfaceClass,
        isInteractive ? [cartContainerInteractiveClass, 'cursor-pointer'].join(' ') : null,
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      data-size={normalizedSize}
      data-variant={normalizedVariant}
      data-state={normalizedState}
      aria-disabled={normalizedState === 'disabled' ? true : undefined}
      {...props}
    >
      {children}
    </div>
  );
});

export default CartContainer;
